import React from 'react';
import {Typography, Box} from '@mui/material';
import {CheckCircleOutlineOutlined, CircleOutlined, FileUpload, HighlightOff, TextSnippet} from '@mui/icons-material';

interface UploadFastqProps {
    selectedFastqFiles: File[];
    handleRemoveFile: (fileName: string) => void;
    handleFASTQUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
    isUploadEnabled: boolean;
}

const UploadFastq = ({selectedFastqFiles, handleRemoveFile, handleFASTQUpload, isUploadEnabled}: UploadFastqProps) => {
    const fastqRef = React.useRef<HTMLInputElement>(null);

    const SelectedFile = ({file}: {file: {name: string}}) => (
        <Box px={3} display="flex" justifyContent="space-between" my={1}>
            <Box display="flex" gap="14px" overflow="auto">
                <TextSnippet />
                <Typography variant="body" size="large">
                    {file.name}
                </Typography>
            </Box>
            <HighlightOff
                sx={{cursor: 'pointer'}}
                onClick={e => {
                    e.stopPropagation();
                    handleRemoveFile(file.name);
                }}
            />
        </Box>
    );

    return (
        <Box
            bgcolor="surface-container-low"
            border="1px dashed"
            borderColor="outline-variant"
            justifyContent="center"
            textAlign="center"
            borderRadius="12px"
            margin="2% 0"
            padding="2%">
            {selectedFastqFiles?.map((file: any) => <SelectedFile file={file} key={file.name} />)}

            <Box
                sx={{cursor: isUploadEnabled ? 'pointer' : 'not-allowed', opacity: isUploadEnabled ? 1 : 0.6}}
                onClick={() => {
                    if (!isUploadEnabled) return;
                    fastqRef.current?.click();
                }}>
                <FileUpload />

                <Typography variant="body" size="medium">
                    Upload FASTQ Files
                </Typography>
                <Typography variant="body" size="small">
                    Supports .fastq.gz files
                </Typography>
                {!isUploadEnabled && (
                    <Typography variant="body" size="small">
                        Upload valid metadata and filenames files first to enable FASTQ upload
                    </Typography>
                )}
                <input
                    ref={fastqRef}
                    type="file"
                    accept=".fastq, .gz, .fq, .fq.gz, .csv"
                    style={{display: 'none'}}
                    onChange={handleFASTQUpload}
                    data-testid="fastq-upload-input"
                    disabled={!isUploadEnabled}
                    multiple
                />
            </Box>
        </Box>
    );
};

interface SampleBulkUploadProps {
    selectedFastqFiles: File[];
    handleRemoveFile: (fileName: string) => void;
    handleFASTQUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
    isUploadEnabled: boolean;
}

/**
 * Enables the user to upload FASTQ files for the experiment.
 */
const SampleBulkUpload = ({selectedFastqFiles, handleRemoveFile, handleFASTQUpload, isUploadEnabled}: SampleBulkUploadProps) => {
    return (
        <>
            <Typography variant="headline" size="small" mt={5}>
                {!selectedFastqFiles?.length ? (
                    <CircleOutlined sx={{fontSize: '24px', color: 'primary-container', marginRight: '7px'}} />
                ) : (
                    <CheckCircleOutlineOutlined sx={{fontSize: '24px', color: 'primary.main', marginRight: '7px'}} />
                )}
                2. Upload your sample files
            </Typography>
            <Typography variant="body" size="medium" px={4} mt={1}>
                Select all samples files to be included in the experiment. <br />
                *NOTE: All sample files should be submitted in their original format as provided by the sequencing organization.
            </Typography>
            <UploadFastq
                handleFASTQUpload={handleFASTQUpload}
                selectedFastqFiles={selectedFastqFiles}
                handleRemoveFile={handleRemoveFile}
                isUploadEnabled={isUploadEnabled}
            />
        </>
    );
};

export default SampleBulkUpload;
